import React, { useState, useMemo, useCallback, useEffect } from "react";
import moment from "moment";
import queries from "../helpers/queries";
import Select from "react-select";
import { useTable } from "react-table";
import { useQuery } from "@apollo/react-hooks";
import exportPerformancePdf from "./exportPerformancePdf";
import exportPerformanceExcel from "./exportPerformanceExcel";
import { FiDownload, FiRefreshCw, FiCalendar, FiUser } from "react-icons/fi";

import {
    LineChart,
    Line,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    BarChart,
    Bar,
    ResponsiveContainer,
} from "recharts";

export const SECTION_STYLES = {
    info: {
        header: "#8a7898",
        cell: "#ffffff",
        cellText: "#222222",
        headerText: "#ffffff",
    },
    sales: {
        header: "#b5a3c4",
        cell: "#ffffff",
        cellText: "#222222",
        headerText: "#ffffff",
    },
    converted: {
        header: "#e8a030",
        cell: "#ffffff",
        cellText: "#222222",
        headerText: "#ffffff",
    },
    cancel: {
        header: "#c8c0d8",
        cell: "#e8e4f0",
        cellText: "#222222",
        headerText: "#333333",
    },
    reject: {
        header: "#9a86b0",
        cell: "#ddd4ea",
        cellText: "#222222",
        headerText: "#ffffff",
    },
    pending: {
        header: "#7eb0d8",
        cell: "#d4e8f8",
        cellText: "#222222",
        headerText: "#ffffff",
    },
    convertedPct: {
        header: "#e8b030",
        cell: "#fff8e8",
        cellText: "#222222",
        headerText: "#ffffff",
    },
};

export const REPORT_THEME = {
    titleBg: "#5a5080",
    titleText: "#ffffff",
    chartBg: "#f8f6fb",
    chartBorder: "#8a7898",
    grid: "#d8d0e4",
    axis: "#444444",
    tooltipBg: "#ffffff",
    tooltipBorder: "#8a7898",
};

export const BRAND_ACCENT = REPORT_THEME.titleBg;

export const CHART_COLORS = {
    all: SECTION_STYLES.sales.header,
    converted: SECTION_STYLES.converted.header,
    cancel: SECTION_STYLES.cancel.header,
    reject: SECTION_STYLES.reject.header,
    pending: SECTION_STYLES.pending.header,
};

export const BAR_SERIES = [
    { key: "allCases", label: "All", color: CHART_COLORS.all },
    { key: "convertedCases", label: "Converted + SignedLO", color: CHART_COLORS.converted },
    { key: "cancelledCases", label: "Cancelled", color: CHART_COLORS.cancel },
    { key: "rejectedCases", label: "Rejected", color: CHART_COLORS.reject },
    { key: "pendingCases", label: "Pending", color: CHART_COLORS.pending },
];

const LINE_SERIES = [
    { key: "allCases", label: "All", color: CHART_COLORS.all },
    { key: "convertedCases", label: "Converted + SignedLO", color: CHART_COLORS.converted },
    { key: "cancelledCases", label: "Cancelled", color: CHART_COLORS.cancel },
    { key: "rejectedCases", label: "Rejected", color: CHART_COLORS.reject },
    { key: "pendingCases", label: "Pending", color: CHART_COLORS.pending },
];

const NumberFormatter = new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const IntegerFormatter = new Intl.NumberFormat("en-GB");

const formatGdv = (value) => {
    if (value === null || value === undefined) {
        return "-";
    }

    return `RM ${NumberFormatter.format(value)}`;
};

const getColumnSection = (column) => column.meta?.section || "info";

const getTotalRowCellBg = (section) => {
    if (section === "convertedPct") {
        return "#e8a030";
    }

    if (section === "pending") {
        return "#4a6898";
    }

    return REPORT_THEME.titleBg;
};

export const PerformanceReportTable = ({ columns, data, loading, nameField = "project" }) => {
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
        useTable({ columns, data });

    if (loading) {
        return (
            <div className="sd-report-empty">
                <div className="sd-report-spinner" />
                <span>Loading performance data...</span>
            </div>
        );
    }

    if (!rows.length) {
        return (
            <div className="sd-report-empty">
                <span>No sales found for the selected period and filters.</span>
            </div>
        );
    }

    return (
        <div className="sd-report-table-scroll">
            <table {...getTableProps()} className="sd-report-table">
                <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => {
                                const section = getColumnSection(column);
                                const sectionStyle = SECTION_STYLES[section];

                                return (
                                    <th
                                        {...column.getHeaderProps()}
                                        style={{
                                            backgroundColor: sectionStyle.header,
                                            color: sectionStyle.headerText,
                                        }}
                                    >
                                        {column.render("Header")}
                                    </th>
                                );
                            })}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {rows.map((row) => {
                        prepareRow(row);
                        const isTotalRow = row.original.isTotalRow;

                        return (
                            <tr
                                {...row.getRowProps()}
                                className={isTotalRow ? "sd-report-total-row" : ""}
                            >
                                {row.cells.map((cell) => {
                                    const section = getColumnSection(cell.column);
                                    const sectionStyle = SECTION_STYLES[section];
                                    const isLabelColumn =
                                        cell.column.id === "rowNumber" ||
                                        cell.column.id === nameField;

                                    return (
                                        <td
                                            {...cell.getCellProps()}
                                            className={isLabelColumn ? "text-left" : "text-right"}
                                            style={{
                                                backgroundColor: isTotalRow
                                                    ? getTotalRowCellBg(section)
                                                    : sectionStyle.cell,
                                                color: isTotalRow
                                                    ? "#ffffff"
                                                    : sectionStyle.cellText,
                                            }}
                                        >
                                            {cell.render("Cell")}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

const ChartLegend = ({ series }) => (
    <div className="sd-report-legend">
        {series.map((item) => (
            <span className="sd-report-legend-item" key={item.key}>
                <span
                    className="sd-report-legend-dot"
                    style={{ backgroundColor: item.color }}
                />
                {item.label}
            </span>
        ))}
    </div>
);

const ChartTooltip = ({ active, payload, nameField = "project", showRates }) => {
    if (!active || !payload || !payload.length) {
        return null;
    }

    const data = payload[0].payload;
    const rows = [
        { label: "All", units: data.allCases, rate: data.allRate, color: CHART_COLORS.all },
        { label: "Converted + SignedLO", units: data.convertedCases, rate: data.conversionRate, color: CHART_COLORS.converted },
        { label: "Cancelled", units: data.cancelledCases, rate: data.cancellationRate, color: CHART_COLORS.cancel },
        { label: "Rejected", units: data.rejectedCases, rate: data.rejectionRate, color: CHART_COLORS.reject },
        { label: "Pending", units: data.pendingCases, rate: data.pendingRate, color: CHART_COLORS.pending },
    ];

    return (
        <div className="sd-report-tooltip">
            <div className="sd-report-tooltip-title">
                {data[nameField] || data.project || data.year}
            </div>
            {rows.map((row) => (
                <div className="sd-report-tooltip-row" key={row.label}>
                    <span className="flex items-center">
                        <span
                            className="sd-report-legend-dot"
                            style={{ backgroundColor: row.color }}
                        />
                        {row.label}
                    </span>
                    <span className="font-semibold">
                        {row.units || 0} units
                        {showRates && row.rate !== undefined && row.rate !== null
                            ? ` · ${NumberFormatter.format(row.rate)}%`
                            : ""}
                    </span>
                </div>
            ))}
        </div>
    );
};

const StatCard = ({ label, units, gdv, color, suffix, unitLabel, footerLabel, footerValue }) => {
    const hasGdv = gdv !== undefined;
    const gdvText = NumberFormatter.format(gdv || 0);

    return (
        <div className="sd-report-stat" style={{ borderTopColor: color }}>
            <div className="sd-report-stat-label">{label}</div>
            <div className="sd-report-stat-value">
                {units}
                {suffix && <span className="sd-report-stat-suffix">{suffix}</span>}
                {unitLabel && <span className="sd-report-stat-unit">{unitLabel}</span>}
            </div>
            <div className="sd-report-stat-footer">
                <span className="sd-report-stat-footer-label">
                    {hasGdv ? "Net GDV" : footerLabel}
                </span>
                <span
                    className="sd-report-stat-footer-value"
                    title={hasGdv ? `RM ${gdvText}` : footerValue}
                >
                    {hasGdv ? (
                        <>
                            <span className="sd-report-stat-currency">RM</span>
                            {gdvText}
                        </>
                    ) : (
                        footerValue
                    )}
                </span>
            </div>
        </div>
    );
};

const PerformanceReportView = ({
    title,
    description,
    bannerTitle,
    dateFrom,
    dateTo,
    filters,
    setFilters,
    onResetFilters,
    periodFilterSection,
    salesDirectorId,
    salesDirectorName = "",
    query = queries.FETCH_PERFORMANCE,
    queryDataKey = "fetchPerformance",
    nameField = "project",
    nameHeader = "Project Name",
    unitsChartTitle = "Units by Project",
    ratesChartTitle = "Conversion Trend by Project",
    pdfNameHeader = "PROJECT NAME",
    pdfSubtitle = "SD Project Performance",
    pdfFilePrefix = "SD-Report",
}) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [states, setStates] = useState([]);
    const [exportingPdf, setExportingPdf] = useState(false);
    const [exportingExcel, setExportingExcel] = useState(false);

    useEffect(() => {
        const checkScreenSize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", checkScreenSize);
        return () => window.removeEventListener("resize", checkScreenSize);
    }, []);

    const filterSelectStyles = useMemo(
        () => ({
            container: (base) => ({
                ...base,
                width: "100%",
            }),
            control: (base, state) => ({
                ...base,
                minHeight: "42px",
                borderRadius: "8px",
                border: "1px solid",
                borderColor: state.isFocused ? BRAND_ACCENT : "rgba(0, 0, 0, 0.1)",
                backgroundColor: "#fff",
                boxShadow: state.isFocused ? "0 0 0 3px rgba(90, 80, 128, 0.2)" : "none",
                "&:hover": {
                    borderColor: state.isFocused ? BRAND_ACCENT : "rgba(0, 0, 0, 0.2)",
                },
            }),
            valueContainer: (base) => ({
                ...base,
                padding: "0 0.75rem",
            }),
            placeholder: (base) => ({
                ...base,
                color: "#9ca3af",
                fontSize: "0.875rem",
            }),
            singleValue: (base) => ({
                ...base,
                color: "#1f1f1f",
                fontSize: "0.875rem",
            }),
            option: (base, state) => ({
                ...base,
                fontSize: "0.875rem",
                backgroundColor: state.isSelected
                    ? BRAND_ACCENT
                    : state.isFocused
                    ? "rgba(90, 80, 128, 0.12)"
                    : "#fff",
                color: state.isSelected ? "#fff" : "#1f1f1f",
            }),
            menu: (base) => ({
                ...base,
                zIndex: 30,
                borderRadius: "8px",
                overflow: "hidden",
            }),
        }),
        []
    );

    const { data: projectsData } = useQuery(queries.FETCH_PROJECTS, {
        fetchPolicy: "network-only",
        variables: {
            limit: 9999,
            page: 1,
            state: "all",
        },
    });

    const projectOptions = useMemo(
        () =>
            (projectsData?.fetchProjectsAgent?.data || []).map((project) => ({
                label: project.name,
                value: project._id,
            })),
        [projectsData]
    );

    useQuery(queries.FETCH_ALL_STATES, {
        fetchPolicy: "network-only",
        onCompleted: ({ fetchStates }) => {
            setStates(fetchStates || []);
        },
    });

    const stateOptions = useMemo(
        () => states.map((state) => ({ label: state.name, value: state._id })),
        [states]
    );

    const { data: performancesData, loading: casesLoading } = useQuery(query, {
        fetchPolicy: "network-only",
        skip: !salesDirectorId,
        variables: {
            limit: 9999,
            page: 1,
            by: "conversionRate",
            dir: "desc",
            project: filters.project ? filters.project.value : undefined,
            salesDirector: salesDirectorId,
            state: filters.state ? filters.state.value : undefined,
            dateFrom: dateFrom || undefined,
            dateTo: dateTo || undefined,
        },
    });

    const tableData = useMemo(() => {
        if (!performancesData) {
            return [];
        }

        const rows = performancesData[queryDataKey]?.data || [];
        const totals = performancesData[queryDataKey]?.totals || [];

        if (rows.length > 0 && totals.length > 0) {
            return [...rows, { ...totals[0], isTotalRow: true }];
        }

        return rows;
    }, [performancesData, queryDataKey]);

    const chartData = useMemo(
        () => performancesData?.[queryDataKey]?.data || [],
        [performancesData, queryDataKey]
    );

    const visibleBarSeries = useMemo(
        () =>
            BAR_SERIES.filter((series) =>
                chartData.some((row) => (row[series.key] || 0) > 0)
            ),
        [chartData]
    );

    const chartHeight = isMobile ? 320 : 460;
    const chartMinWidth = Math.max(chartData.length * (isMobile ? 110 : 180), 320);

    const directorInfo = useMemo(
        () => ({ label: salesDirectorName, type: "SALES DIRECTOR" }),
        [salesDirectorName]
    );

    const performanceTotals = useMemo(
        () => performancesData?.[queryDataKey]?.totals?.[0] || null,
        [performancesData, queryDataKey]
    );

    const getExportPayload = useCallback(
        () => ({
            rows: chartData,
            totals: performanceTotals,
            dateFrom,
            dateTo,
            directorLabel: directorInfo.label,
            directorType: directorInfo.type,
            nameField,
            nameHeader: pdfNameHeader,
            reportSubtitle: pdfSubtitle,
            filePrefix: pdfFilePrefix,
        }),
        [
            chartData,
            performanceTotals,
            dateFrom,
            dateTo,
            directorInfo,
            nameField,
            pdfNameHeader,
            pdfSubtitle,
            pdfFilePrefix,
        ]
    );

    const handleExportPdf = useCallback(async () => {
        if (!chartData.length) {
            alert("No performance data to export.");
            return;
        }

        setExportingPdf(true);

        try {
            await exportPerformancePdf(getExportPayload());
        } finally {
            setExportingPdf(false);
        }
    }, [chartData.length, getExportPayload]);

    const handleExportExcel = useCallback(async () => {
        if (!chartData.length) {
            alert("No performance data to export.");
            return;
        }

        setExportingExcel(true);

        try {
            await exportPerformanceExcel(getExportPayload());
        } finally {
            setExportingExcel(false);
        }
    }, [chartData.length, getExportPayload]);

    const exportDisabled =
        exportingPdf || exportingExcel || casesLoading || chartData.length === 0;

    const periodLabel =
        dateFrom && dateTo
            ? `${moment(dateFrom).format("D MMM YYYY")} – ${moment(dateTo).format("D MMM YYYY")}`
            : "All time";

    const statCards = [
        {
            label: "Total Sales",
            units: IntegerFormatter.format(performanceTotals?.allCases || 0),
            gdv: performanceTotals?.totalNetGdv,
            color: CHART_COLORS.all,
        },
        {
            label: "Converted + SignedLO",
            units: IntegerFormatter.format(performanceTotals?.convertedCases || 0),
            gdv: performanceTotals?.convertNetGdv,
            color: CHART_COLORS.converted,
        },
        {
            label: "Cancelled",
            units: IntegerFormatter.format(performanceTotals?.cancelledCases || 0),
            gdv: performanceTotals?.cancelledNetGdv,
            color: CHART_COLORS.cancel,
        },
        {
            label: "Loan Rejected",
            units: IntegerFormatter.format(performanceTotals?.rejectedCases || 0),
            gdv: performanceTotals?.rejectedNetGdv,
            color: CHART_COLORS.reject,
        },
        {
            label: "Pending",
            units: IntegerFormatter.format(performanceTotals?.pendingCases || 0),
            gdv: performanceTotals?.pendingNetGdv,
            color: CHART_COLORS.pending,
        },
        {
            label: "Conversion Rate",
            units: NumberFormatter.format(performanceTotals?.conversionRate || 0),
            suffix: "%",
            color: SECTION_STYLES.convertedPct.header,
            footerLabel: "Converted / Total",
            footerValue: `${IntegerFormatter.format(performanceTotals?.convertedCases || 0)} / ${IntegerFormatter.format(performanceTotals?.allCases || 0)} units`,
        },
    ].map((card) => (card.suffix ? card : { ...card, unitLabel: "units" }));

    const columns = useMemo(
        () => [
            {
                Header: "No.",
                id: "rowNumber",
                meta: { section: "info" },
                Cell: ({ row }) => {
                    if (row.original.isTotalRow) {
                        return <div className="font-bold">TOTAL</div>;
                    }

                    return <div>{row.index + 1}</div>;
                },
            },
            {
                Header: nameHeader,
                accessor: nameField,
                meta: { section: "info" },
                Cell: ({ value, row }) => (
                    <div className={row.original.isTotalRow ? "font-bold" : "font-semibold"}>
                        {value}
                    </div>
                ),
            },
            {
                Header: "Sales",
                meta: { section: "sales" },
                columns: [
                    {
                        Header: "Units",
                        accessor: "allCases",
                        meta: { section: "sales" },
                        Cell: ({ value }) => <div>{value || 0}</div>,
                    },
                    {
                        Header: "Gross GDV",
                        accessor: "totalGrossGdv",
                        meta: { section: "sales" },
                        Cell: ({ value }) => <div>{formatGdv(value)}</div>,
                    },
                    {
                        Header: "Net GDV",
                        accessor: "totalNetGdv",
                        meta: { section: "sales" },
                        Cell: ({ value }) => <div>{formatGdv(value)}</div>,
                    },
                ],
            },
            {
                Header: "Converted + SignedLO",
                meta: { section: "converted" },
                columns: [
                    {
                        Header: "Units",
                        accessor: "convertedCases",
                        meta: { section: "converted" },
                        Cell: ({ value }) => <div>{value || 0}</div>,
                    },
                    {
                        Header: "Gross GDV",
                        accessor: "convertGrossGdv",
                        meta: { section: "converted" },
                        Cell: ({ value }) => <div>{formatGdv(value)}</div>,
                    },
                    {
                        Header: "Net GDV",
                        accessor: "convertNetGdv",
                        meta: { section: "converted" },
                        Cell: ({ value }) => <div>{formatGdv(value)}</div>,
                    },
                ],
            },
            {
                Header: "Cancel",
                meta: { section: "cancel" },
                columns: [
                    {
                        Header: "Units",
                        accessor: "cancelledCases",
                        meta: { section: "cancel" },
                        Cell: ({ value }) => <div>{value || 0}</div>,
                    },
                    {
                        Header: "Gross GDV",
                        accessor: "cancelledGrossGdv",
                        meta: { section: "cancel" },
                        Cell: ({ value }) => <div>{formatGdv(value)}</div>,
                    },
                    {
                        Header: "Net GDV",
                        accessor: "cancelledNetGdv",
                        meta: { section: "cancel" },
                        Cell: ({ value }) => <div>{formatGdv(value)}</div>,
                    },
                ],
            },
            {
                Header: "Loan Reject",
                meta: { section: "reject" },
                columns: [
                    {
                        Header: "Units",
                        accessor: "rejectedCases",
                        meta: { section: "reject" },
                        Cell: ({ value }) => <div>{value || 0}</div>,
                    },
                    {
                        Header: "Gross GDV",
                        accessor: "rejectedGrossGdv",
                        meta: { section: "reject" },
                        Cell: ({ value }) => <div>{formatGdv(value)}</div>,
                    },
                    {
                        Header: "Net GDV",
                        accessor: "rejectedNetGdv",
                        meta: { section: "reject" },
                        Cell: ({ value }) => <div>{formatGdv(value)}</div>,
                    },
                ],
            },
            {
                Header: "Pending",
                meta: { section: "pending" },
                columns: [
                    {
                        Header: "Units",
                        accessor: "pendingCases",
                        meta: { section: "pending" },
                        Cell: ({ value }) => <div>{value || 0}</div>,
                    },
                    {
                        Header: "Gross GDV",
                        accessor: "pendingGrossGdv",
                        meta: { section: "pending" },
                        Cell: ({ value }) => <div>{formatGdv(value)}</div>,
                    },
                    {
                        Header: "Net GDV",
                        accessor: "pendingNetGdv",
                        meta: { section: "pending" },
                        Cell: ({ value }) => <div>{formatGdv(value)}</div>,
                    },
                ],
            },
            {
                Header: "Converted + SignedLO %",
                accessor: "conversionRate",
                meta: { section: "convertedPct" },
                Cell: ({ value }) => (
                    <div className="font-bold">{NumberFormatter.format(value || 0)}%</div>
                ),
            },
        ],
        [nameField, nameHeader]
    );

    const axisTick = { fontSize: isMobile ? 10 : 12, fill: REPORT_THEME.axis };

    return (
        <div className="sd-report my-10 lg:my-0 lg:p-8">
            <section className="sd-report-card">
                <div className="sd-report-header">
                    <div>
                        <h1 className="sd-report-title">{title}</h1>
                        {description && (
                            <p className="sd-report-subtitle">{description}</p>
                        )}
                        <div className="sd-report-badges">
                            {salesDirectorName && (
                                <span className="sd-report-badge">
                                    <FiUser size={13} aria-hidden />
                                    {salesDirectorName}
                                </span>
                            )}
                            <span className="sd-report-badge sd-report-badge-muted">
                                <FiCalendar size={13} aria-hidden />
                                {periodLabel}
                            </span>
                        </div>
                    </div>
                    <div className="sd-report-actions">
                        <button
                            type="button"
                            className="sd-report-btn sd-report-btn-pdf"
                            disabled={exportDisabled}
                            onClick={handleExportPdf}
                        >
                            <FiDownload
                                className={exportingPdf ? "animate-pulse" : ""}
                                size={16}
                                aria-hidden
                            />
                            {exportingPdf ? "Exporting..." : "Export PDF"}
                        </button>
                        <button
                            type="button"
                            className="sd-report-btn sd-report-btn-excel"
                            disabled={exportDisabled}
                            onClick={handleExportExcel}
                        >
                            <FiDownload
                                className={exportingExcel ? "animate-pulse" : ""}
                                size={16}
                                aria-hidden
                            />
                            {exportingExcel ? "Exporting..." : "Export Excel"}
                        </button>
                    </div>
                </div>

                <div className="sd-report-filters">
                    {periodFilterSection}
                    <div className="sd-report-field">
                        <label className="sd-report-label">Project</label>
                        <Select
                            styles={filterSelectStyles}
                            isSearchable={true}
                            isClearable={true}
                            placeholder="All projects"
                            options={projectOptions}
                            value={filters.project}
                            onChange={(val) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    project: val,
                                }))
                            }
                        />
                    </div>
                    <div className="sd-report-field">
                        <label className="sd-report-label">State</label>
                        <Select
                            styles={filterSelectStyles}
                            isSearchable={false}
                            isClearable={true}
                            options={stateOptions}
                            value={filters.state}
                            placeholder="All states"
                            onChange={(val) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    state: val,
                                }))
                            }
                        />
                    </div>
                    <div className="sd-report-field sd-report-field-action">
                        <button
                            type="button"
                            className="sd-report-btn sd-report-btn-reset"
                            onClick={onResetFilters}
                        >
                            <FiRefreshCw size={15} aria-hidden />
                            Reset
                        </button>
                    </div>
                </div>

                <div className="sd-report-stats">
                    {statCards.map((card) => (
                        <StatCard key={card.label} {...card} />
                    ))}
                </div>
            </section>

            <section className="sd-report-card">
                <div className="sd-report-section-header">
                    <div>
                        <h2 className="sd-report-section-title">Project Performance</h2>
                        <p className="sd-report-section-subtitle">
                            D&amp;D &apos; KL HQ — {bannerTitle}
                        </p>
                    </div>
                    {!casesLoading && chartData.length > 0 && (
                        <span className="sd-report-badge sd-report-badge-muted">
                            {chartData.length} project{chartData.length === 1 ? "" : "s"}
                        </span>
                    )}
                </div>
                <PerformanceReportTable
                    columns={columns}
                    data={tableData}
                    loading={casesLoading}
                    nameField={nameField}
                />
            </section>

            {!casesLoading && chartData.length > 0 && (
                <div className="sd-report-charts">
                    <section className="sd-report-card">
                        <div className="sd-report-section-header">
                            <h2 className="sd-report-section-title">{unitsChartTitle}</h2>
                        </div>
                        <div className="sd-report-chart-scroll">
                            <div style={{ minWidth: chartMinWidth }}>
                                <ResponsiveContainer width="100%" height={chartHeight}>
                                    <BarChart
                                        data={chartData}
                                        barCategoryGap="25%"
                                        barGap={4}
                                        maxBarSize={28}
                                        margin={{ top: 10, right: 10, bottom: isMobile ? 40 : 10, left: 0 }}
                                    >
                                        <CartesianGrid stroke={REPORT_THEME.grid} vertical={false} />
                                        <XAxis
                                            dataKey={nameField}
                                            stroke={REPORT_THEME.grid}
                                            interval={0}
                                            tick={axisTick}
                                            tickLine={false}
                                            angle={isMobile ? -35 : 0}
                                            textAnchor={isMobile ? "end" : "middle"}
                                            height={isMobile ? 60 : 36}
                                        />
                                        <YAxis
                                            stroke={REPORT_THEME.grid}
                                            tick={axisTick}
                                            tickLine={false}
                                            width={40}
                                            allowDecimals={false}
                                        />
                                        <Tooltip
                                            cursor={{ fill: "rgba(90, 80, 128, 0.08)" }}
                                            content={<ChartTooltip nameField={nameField} />}
                                        />
                                        {visibleBarSeries.map((series) => (
                                            <Bar
                                                key={series.key}
                                                dataKey={series.key}
                                                name={series.label}
                                                fill={series.color}
                                                radius={[4, 4, 0, 0]}
                                            />
                                        ))}
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                        <ChartLegend series={visibleBarSeries} />
                    </section>

                    <section className="sd-report-card">
                        <div className="sd-report-section-header">
                            <h2 className="sd-report-section-title">{ratesChartTitle}</h2>
                        </div>
                        <div className="sd-report-chart-scroll">
                            <div style={{ minWidth: chartMinWidth }}>
                                <ResponsiveContainer width="100%" height={chartHeight}>
                                    <LineChart
                                        data={chartData}
                                        margin={{ top: 10, right: 20, bottom: isMobile ? 40 : 10, left: 0 }}
                                    >
                                        <CartesianGrid stroke={REPORT_THEME.grid} vertical={false} />
                                        <XAxis
                                            dataKey={nameField}
                                            stroke={REPORT_THEME.grid}
                                            interval={0}
                                            tick={axisTick}
                                            tickLine={false}
                                            angle={isMobile ? -35 : 0}
                                            textAnchor={isMobile ? "end" : "middle"}
                                            height={isMobile ? 60 : 36}
                                        />
                                        <YAxis
                                            stroke={REPORT_THEME.grid}
                                            tick={axisTick}
                                            tickLine={false}
                                            width={40}
                                            allowDecimals={false}
                                        />
                                        <Tooltip content={<ChartTooltip nameField={nameField} showRates />} />
                                        {LINE_SERIES.map((series) => (
                                            <Line
                                                key={series.key}
                                                type="monotone"
                                                dataKey={series.key}
                                                name={series.label}
                                                stroke={series.color}
                                                strokeWidth={3}
                                                dot={{ r: 3, strokeWidth: 0, fill: series.color }}
                                                activeDot={{ r: 5 }}
                                            />
                                        ))}
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                        <ChartLegend series={LINE_SERIES} />
                    </section>
                </div>
            )}
        </div>
    );
};

export default PerformanceReportView;
